/**
 * CategoryEditSheet — bottom sheet (mobile) / modal (desktop) for editing
 * or creating a single category.
 *
 * Pixel-faithful port of `CategoryEditSheet` in
 * `design/project/screens-misc.jsx`.
 *
 * Modes:
 *   - `create`: name + icon + color, no delete affordance.
 *   - `edit`:   prefilled from `initial`. If the row is `is_system=true`
 *     we show a non-deletable hint instead of the delete button.
 *
 * Reference-check flow:
 *   - Delete button is async — when pressed we show a small spinner while
 *     `refCheck()` runs. If the count is non-zero, we surface an inline
 *     hint: "此分類被 N 筆紀錄 / M 個固定週期使用，無法刪除"
 *     (consistent with the spec text). Otherwise we call `onDelete()`
 *     which the parent wires to the soft-delete repo + sync queue.
 *
 * The reference check itself is performed by the parent (it has access to
 * the transactions / recurring slices). We only orchestrate the UX.
 */

import {
  useMemo,
  useState,
  type CSSProperties,
} from 'react';

import BottomSheet from '@/components/common/BottomSheet';
import Modal from '@/components/common/Modal';
import Icon from '@/components/common/Icon';
import IconPicker from '@/components/common/IconPicker';
import type { CategoryRow, CategoryKind } from '@/lib/sheets/types';
import type { RefCheck } from '@/state/slices/categories';

export type CategoryEditMode = 'create' | 'edit';
export type CategoryEditVariant = 'sheet' | 'modal';

export interface CategoryEditSheetSubmit {
  name: string;
  color_token: string;
  icon: string;
  kind: CategoryKind;
  parent_id?: string;
}

export interface CategoryEditSheetProps {
  mode: CategoryEditMode;
  /** Required when mode === 'edit'. */
  initial?: CategoryRow;
  /** When set, this row will be created as a sub-category of `parent_id`. */
  parent_id?: string;
  /** Initial kind for create-mode. Edit-mode inherits from initial. */
  defaultKind?: CategoryKind;
  refCheck: RefCheck;
  onSubmit: (input: CategoryEditSheetSubmit) => Promise<void> | void;
  onDelete?: () => Promise<void> | void;
  onClose: () => void;
  variant?: CategoryEditVariant;
}

function normalizeIconValue(raw: string | undefined | null): string {
  if (!raw) return 'lucide:tag';
  if (raw.startsWith('lucide:') || raw.startsWith('emoji:')) return raw;
  return `lucide:${raw}`;
}

const COLOR_OPTIONS: ReadonlyArray<string> = [
  '--cat-food',
  '--cat-transit',
  '--cat-home',
  '--cat-daily',
  '--cat-fun',
  '--cat-health',
  '--cat-pet',
  '--cat-travel',
];

function tokenToCss(token: string): string {
  if (token.startsWith('--')) return `var(${token})`;
  return token;
}

const LABEL_STYLE: CSSProperties = {
  font: '500 11px var(--font-cjk)',
  color: 'var(--fg-3)',
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  padding: '0 2px 6px',
};

function CategoryEditBody(props: CategoryEditSheetProps): JSX.Element {
  const {
    mode,
    initial,
    parent_id,
    defaultKind,
    refCheck,
    onSubmit,
    onDelete,
    onClose,
  } = props;

  const isSystem = !!initial?.is_system;

  const [name, setName] = useState<string>(initial?.name ?? '');
  const [icon, setIcon] = useState<string>(() => normalizeIconValue(initial?.icon));
  const [color, setColor] = useState<string>(
    initial?.color_token ?? COLOR_OPTIONS[0]!,
  );
  const [kind] = useState<CategoryKind>(
    initial?.kind ?? defaultKind ?? 'expense',
  );

  // Reference-check + delete UX
  const [deleting, setDeleting] = useState(false);
  const [refError, setRefError] = useState<string | null>(null);

  const canSave = name.trim().length > 0;

  // Edit mode reads parent_id from `initial` (the row being edited);
  // create mode reads it from props (passed by the parent when opening
  // the sheet for "新增子分類"). Without this fork, editing a sub-category
  // dropped its parent_id and the row promoted itself to a top-level
  // category — see screenshot bug report #6.
  const effectiveParentId = initial?.parent_id ?? parent_id;

  const submit = async (): Promise<void> => {
    if (!canSave) return;
    await onSubmit({
      name: name.trim(),
      color_token: color,
      icon,
      kind,
      parent_id: effectiveParentId,
    });
  };

  const triggerDelete = async (): Promise<void> => {
    if (!onDelete || isSystem) return;
    setDeleting(true);
    setRefError(null);
    try {
      const counts = await refCheck();
      if (counts.txCount > 0 || counts.recurringCount > 0) {
        setRefError(
          `此分類被 ${counts.txCount} 筆紀錄、${counts.recurringCount} 個固定週期使用，無法刪除`,
        );
        return;
      }
      await onDelete();
    } finally {
      setDeleting(false);
    }
  };

  const titleText = useMemo(
    () => (mode === 'edit' ? '編輯分類' : '新增分類'),
    [mode],
  );

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 12,
        }}
      >
        <button
          type="button"
          onClick={onClose}
          style={{
            background: 'transparent',
            border: 0,
            cursor: 'pointer',
            font: '500 15px var(--font-cjk)',
            color: 'var(--fg-3)',
          }}
        >
          取消
        </button>
        <div style={{ font: '600 16px var(--font-cjk)', color: 'var(--fg-1)' }}>
          {titleText}
        </div>
        <button
          type="button"
          onClick={() => void submit()}
          disabled={!canSave}
          style={{
            background: 'transparent',
            border: 0,
            cursor: canSave ? 'pointer' : 'default',
            font: '600 15px var(--font-cjk)',
            color: canSave ? 'var(--accent)' : 'var(--fg-3)',
          }}
        >
          儲存
        </button>
      </div>

      {/* Preview */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          padding: '10px 0 16px',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            width: 64,
            height: 64,
            borderRadius: 18,
            background: tokenToCss(color),
            display: 'grid',
            placeItems: 'center',
            boxShadow: 'var(--shadow-2)',
          }}
        >
          <Icon value={icon} size={28} stroke="#fff" />
        </div>
      </div>

      <div style={LABEL_STYLE}>名稱</div>
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="例：寵物"
        style={{
          width: '100%',
          padding: '12px 14px',
          border: '0.5px solid var(--border-1)',
          borderRadius: 12,
          background: 'var(--bg-surface)',
          font: '500 15px var(--font-cjk)',
          color: 'var(--fg-1)',
          outline: 'none',
          boxSizing: 'border-box',
        }}
      />

      <div style={{ ...LABEL_STYLE, paddingTop: 16 }}>圖示</div>
      <IconPicker value={icon} onChange={setIcon} />

      <div style={{ ...LABEL_STYLE, paddingTop: 16 }}>顏色</div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(8, 1fr)',
          gap: 8,
        }}
      >
        {COLOR_OPTIONS.map((co) => {
          const on = color === co;
          return (
            <button
              key={co}
              type="button"
              aria-label={co}
              aria-pressed={on}
              onClick={() => setColor(co)}
              style={{
                aspectRatio: '1',
                borderRadius: 10,
                background: tokenToCss(co),
                border: '2px solid',
                borderColor: on ? 'var(--fg-1)' : 'transparent',
                cursor: 'pointer',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              {on && <Icon name="check" size={14} stroke="#fff" />}
            </button>
          );
        })}
      </div>

      {/* Delete area */}
      {mode === 'edit' && (
        <div style={{ marginTop: 22 }}>
          {isSystem ? (
            <div
              style={{
                font: '400 12px/1.6 var(--font-cjk)',
                color: 'var(--fg-3)',
                background: 'var(--bg-raised)',
                borderRadius: 10,
                padding: '12px 14px',
              }}
            >
              預設分類無法刪除，可改名或重新著色。
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={() => void triggerDelete()}
                disabled={deleting}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'transparent',
                  color: 'var(--danger)',
                  border: '1px solid var(--danger)',
                  borderRadius: 12,
                  cursor: deleting ? 'progress' : 'pointer',
                  font: '500 14px var(--font-cjk)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  opacity: deleting ? 0.7 : 1,
                }}
              >
                {deleting ? <Spinner /> : <Icon name="trash" size={16} stroke="var(--danger)" />}
                {deleting ? '檢查使用中…' : '刪除'}
              </button>
              {refError && (
                <div
                  role="alert"
                  style={{
                    marginTop: 10,
                    padding: '10px 12px',
                    borderRadius: 10,
                    background: 'rgba(146,53,38,0.08)',
                    color: 'var(--danger)',
                    font: '400 12px/1.55 var(--font-cjk)',
                  }}
                >
                  {refError}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Spinner(): JSX.Element {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 14,
        height: 14,
        borderRadius: '50%',
        border: '2px solid var(--danger)',
        borderTopColor: 'transparent',
        animation: 'mochia-spin 0.8s linear infinite',
        display: 'inline-block',
      }}
    />
  );
}

export default function CategoryEditSheet(
  props: CategoryEditSheetProps,
): JSX.Element {
  const variant = props.variant ?? 'sheet';
  const body = <CategoryEditBody {...props} />;
  if (variant === 'modal') {
    return (
      <Modal open={true} onClose={props.onClose} width={460}>
        {body}
      </Modal>
    );
  }
  return (
    <BottomSheet open={true} onClose={props.onClose}>
      {body}
    </BottomSheet>
  );
}
